import React, { FunctionComponent } from 'react';
import { Box, Paper, Grid, Typography } from '@mui/material';
import {
  CheckCircleOutline as CheckCircleIcon,
  ErrorOutline as ErrorIcon,
  Rule as RuleIcon,
  PlayCircleOutline as ActionIcon,
} from '@mui/icons-material';
import { AuditTrailResponse } from '../../../types/claim';

interface MetricsCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
}

const MetricsCard: FunctionComponent<MetricsCardProps> = ({
  title,
  value,
  icon,
  color,
}): JSX.Element => (
  <Paper
    elevation={0}
    sx={{
      p: 2,
      height: '100%',
      backgroundColor: `${color}08`,
      border: `1px solid ${color}20`,
      borderRadius: '8px',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <Box sx={{ color: color }}>{icon}</Box>
      <Box>
        <Typography variant="body2" color="text.secondary">
          {title}
        </Typography>
        <Typography variant="h6" sx={{ color: color, fontWeight: 600 }}>
          {value}
        </Typography>
      </Box>
    </Box>
  </Paper>
);

const AuditTrailMetrics: FunctionComponent<AuditTrailResponse> = ({
  entries,
}): JSX.Element => {
  // Calculate metrics
  const totalEntries = entries.length;
  const ruleEntries = entries.filter((entry) => entry.type === 'rule');
  const actionEntries = entries.filter((entry) => entry.type === 'action');

  const passedEntries = entries.filter((entry) => entry.result === true);
  const failedEntries = entries.filter((entry) => entry.result === false);

  const metrics = [
    {
      title: 'Rules Evaluated',
      value: ruleEntries.length,
      icon: <RuleIcon />,
      color: '#28C76F',
    },
    {
      title: 'Actions Executed',
      value: actionEntries.length,
      icon: <ActionIcon />,
      color: '#7367F0',
    },
    {
      title: 'Passed',
      value: passedEntries.length,
      icon: <CheckCircleIcon />,
      color: '#2E7D32',
    },
    {
      title: 'Failed',
      value: failedEntries.length,
      icon: <ErrorIcon />,
      color: '#DC3545',
    },
  ];

  return (
    <Box sx={{ mb: 3 }}>
      <Grid container spacing={2}>
        {metrics.map((metric, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <MetricsCard {...metric} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default AuditTrailMetrics;
